import {useSelector} from 'react-redux';

import appConfig from '@/config/appConfig.typed';
import {AEM_DEVICE_SETTINGS_TYPES} from '@/consts/aem/aemDeviceSettingsTypes';
import log from '@/services/logger/log';
import {selectDeviceFirmwareVersion, selectHolderFirmwareVersion} from '@/state/selectors/device';
import {selectMwDeviceType} from '@/state/selectors/mw';
import versionUtils from '@/utils/versionUtils';

const VERSION_COMPARISON = {
    GREATER_OR_EQUAL: '>=',
    GREATER: '>',
    EQUAL: '=',
    LESS_OR_EQUAL: '<=',
    LESS: '<',
};

const VERSION_COMPARISON_FUNCTIONS = {
    [VERSION_COMPARISON.GREATER_OR_EQUAL]: versionUtils.isFirstVersionGreaterOrEqual,
    [VERSION_COMPARISON.GREATER]: versionUtils.isFirstVersionGreater,
    [VERSION_COMPARISON.EQUAL]: versionUtils.isFirstVersionEqual,
    [VERSION_COMPARISON.LESS_OR_EQUAL]: versionUtils.isFirstVersionLessOrEqual,
    [VERSION_COMPARISON.LESS]: versionUtils.isFirstVersionLess,
};

const isSettingEnabled = ({configSettings, settingType, holderFirmwareVersion, deviceFirmwareVersion}) => {
    const setting = configSettings.find((x) => x.settingType === settingType);

    if (!setting) return false;

    const {firmwareRestrictions} = setting;

    if (!firmwareRestrictions?.length) return true;

    return firmwareRestrictions.some(({comparisonType, isHolderRelated, value, version}) => {
        const currentVersion = isHolderRelated ? holderFirmwareVersion : deviceFirmwareVersion;

        if (!currentVersion) return true;

        const compareFn = VERSION_COMPARISON_FUNCTIONS[comparisonType];

        if (!compareFn) {
            log.warn(`Unknown comparison type: ${comparisonType}`);
            return false;
        }

        return compareFn(currentVersion, version) ? value : false;
    });
};

const useDeviceSettingsBase = (type) => {
    const deviceFirmwareVersion = useSelector(selectDeviceFirmwareVersion);
    const holderFirmwareVersion = useSelector(selectHolderFirmwareVersion);
    const deviceType = useSelector(selectMwDeviceType);
    const configSettings = appConfig.getDeviceSettings(type || deviceType);

    const getIsSettingEnabled = (settingType) =>
        isSettingEnabled({
            configSettings,
            settingType,
            deviceFirmwareVersion,
            holderFirmwareVersion,
        });

    return {getIsSettingEnabled};
};

export const useLockEnabled = (type) => {
    const {getIsSettingEnabled} = useDeviceSettingsBase(type);

    return getIsSettingEnabled(AEM_DEVICE_SETTINGS_TYPES.LOCK_UNLOCK);
};

export const useRemainingSticksEnabled = (type) => {
    const {getIsSettingEnabled} = useDeviceSettingsBase(type);

    return getIsSettingEnabled(AEM_DEVICE_SETTINGS_TYPES.REMAINING_STICKS);
};

const useDeviceSettingsEnabled = ({type}) => {
    const {getIsSettingEnabled} = useDeviceSettingsBase(type);

    const settings = {
        isAemFlexBatteryEnabled: getIsSettingEnabled(AEM_DEVICE_SETTINGS_TYPES.FLEX_BATTERY),
        isPauseModeEnabled: getIsSettingEnabled(AEM_DEVICE_SETTINGS_TYPES.PAUSE_MODE),
        isAutoStartEnabled: getIsSettingEnabled(AEM_DEVICE_SETTINGS_TYPES.AUTO_START),
        isFlexPuffEnabled: getIsSettingEnabled(AEM_DEVICE_SETTINGS_TYPES.FLEX_PUFF),
        isSmartGesturesEnabled: getIsSettingEnabled(AEM_DEVICE_SETTINGS_TYPES.SMART_GESTURES),
        isIlluminationModeEnabled: getIsSettingEnabled(AEM_DEVICE_SETTINGS_TYPES.ILLUMINATION_MODE),
        isVibrationEnabled: getIsSettingEnabled(AEM_DEVICE_SETTINGS_TYPES.VIBRATIONS),
        isLockEnabled: getIsSettingEnabled(AEM_DEVICE_SETTINGS_TYPES.LOCK_UNLOCK),
        isRemainingSticksEnabled: getIsSettingEnabled(AEM_DEVICE_SETTINGS_TYPES.REMAINING_STICKS),
    };

    const isAnySettingEnabled = Object.values(settings).some(Boolean);

    return {
        ...settings,
        isAnySettingEnabled,
    };
};

export default useDeviceSettingsEnabled;
